import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageComponent, ComponentData, SupportedLanguage } from '../../../../../services/website.service';
import { TextComponentComponent } from '../text-component/text-component.component';
import { ProductsComponentComponent } from '../products-component/products-component.component';
import { MediaGalleryComponentComponent } from '../media-gallery-component/media-gallery-component.component';
import { NewsComponentComponent } from '../news-component/news-component.component';
import { ProjectsComponentComponent } from '../projects-component/projects-component.component';
import { FormComponentComponent } from '../form-component/form-component.component';
import { MapComponentComponent } from '../map-component/map-component.component';

export interface GridCell {
  id: string;
  components: PageComponent[];
  colSpan?: number;
  rowSpan?: number;
}

@Component({
  selector: 'app-grid-component',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    TextComponentComponent,
    ProductsComponentComponent,
    MediaGalleryComponentComponent,
    NewsComponentComponent,
    ProjectsComponentComponent,
    FormComponentComponent,
    MapComponentComponent
  ],
  templateUrl: './grid-component.component.html',
  styleUrls: ['./grid-component.component.scss']
})
export class GridComponentComponent {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() isSelected: boolean = false;
  @Input() currentLocale: SupportedLanguage = 'nl';
  /** Passed through to child components (e.g. news) for loading domain-specific data. */
  @Input() domainOverride?: string;
  @Output() styleChange = new EventEmitter<void>();
  @Output() cellSelected = new EventEmitter<{ cellIndex: number, componentIndex?: number }>();
  @Output() addComponentToCell = new EventEmitter<{ cellIndex: number, componentType: string }>();
  @Output() componentInCellSelected = new EventEmitter<{ cellIndex: number, componentIndex: number }>();

  selectedCellIndex: number | null = null;

  // Getters for grid settings
  getColumns(): number {
    return this.component.data['columns'] || 3;
  }

  getRows(): number {
    return this.component.data['rows'] || 1;
  }

  getGap(): number {
    return this.component.data['gap'] || 16;
  }

  getGapUnit(): string {
    return this.component.data['gapUnit'] || 'px';
  }

  getHeading(): string {
    return this.component.data['heading'] || '';
  }

  getCells(): GridCell[] {
    return this.component.data['cells'] || [];
  }

  getGridLayout(): string {
    return this.component.data['gridLayout'] || 'equal';
  }

  getCustomColumnWidths(): string {
    return this.component.data['customColumnWidths'] || '';
  }

  getItemPadding(): number {
    return this.component.data['itemPadding'] || 16;
  }

  getItemBorderRadius(): number {
    return this.component.data['itemBorderRadius'] || 8;
  }

  getItemBackgroundColor(): string {
    return this.component.data['itemBackgroundColor'] || '#f9fafb';
  }

  getShowItemBorder(): boolean {
    return this.component.data['showItemBorder'] !== false;
  }

  getItemBorderColor(): string {
    return this.component.data['itemBorderColor'] || '#e5e7eb';
  }

  // Styling getters (shared pattern)
  getBackgroundType(): string {
    return this.component.data['backgroundType'] || 'solid';
  }

  getBackgroundColor(): string {
    return this.component.data['backgroundColor'] || '#ffffff';
  }

  getGradientFrom(): string {
    return this.component.data['gradientFrom'] || '#ffffff';
  }

  getGradientTo(): string {
    return this.component.data['gradientTo'] || '#ffffff';
  }

  getBackgroundImage(): string {
    return this.component.data['backgroundImage'] || '';
  }

  getTitlePosition(): string {
    return this.component.data['titlePosition'] || 'left';
  }

  getHeadingColor(): string {
    return this.component.data['headingColor'] || '#111827';
  }

  getTextColor(): string {
    return this.component.data['textColor'] || '#374151';
  }

  getParallax(): boolean {
    return this.component.data['parallax'] || false;
  }

  getSectionHeight(): string {
    return this.component.data['sectionHeight'] || '';
  }

  getHeightUnit(): string {
    return this.component.data['heightUnit'] || 'px';
  }

  getPreviewStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const backgroundType = this.getBackgroundType();

    if (backgroundType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (backgroundType === 'gradient') {
      styles['background'] = `linear-gradient(to bottom, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (backgroundType === 'image') {
      const bgImage = this.getBackgroundImage();
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
        if (this.getParallax()) {
          styles['background-attachment'] = 'fixed';
        }
      }
    }

    const sectionHeight = this.getSectionHeight();
    if (sectionHeight) {
      const unit = this.getHeightUnit();
      styles['min-height'] = `${sectionHeight}${unit}`;
    }

    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch (position) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  }

  getGridStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const gap = this.getGap();
    const gapUnit = this.getGapUnit();
    const layout = this.getGridLayout();
    const columns = this.getColumns();

    styles['gap'] = `${gap}${gapUnit}`;

    if (layout === 'equal') {
      styles['grid-template-columns'] = `repeat(${columns}, 1fr)`;
    } else if (layout === 'custom' && this.getCustomColumnWidths()) {
      styles['grid-template-columns'] = this.getCustomColumnWidths();
    } else {
      styles['grid-template-columns'] = `repeat(${columns}, 1fr)`;
    }

    return styles;
  }

  getGridItemStyles(cell?: GridCell): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    styles['padding'] = `${this.getItemPadding()}px`;
    styles['border-radius'] = `${this.getItemBorderRadius()}px`;
    styles['background-color'] = this.getItemBackgroundColor();

    if (this.getShowItemBorder()) {
      styles['border'] = `1px solid ${this.getItemBorderColor()}`;
    }

    // Handle col/row span
    if (cell?.colSpan && cell.colSpan > 1) {
      styles['grid-column'] = `span ${cell.colSpan}`;
    }
    if (cell?.rowSpan && cell.rowSpan > 1) {
      styles['grid-row'] = `span ${cell.rowSpan}`;
    }

    return styles;
  }

  getGridColumnsClass(): string {
    const cols = this.getColumns();
    const gridClasses: { [key: number]: string } = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5',
      6: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-6',
    };
    return gridClasses[cols] || gridClasses[3];
  }

  hasCells(): boolean {
    const cells = this.getCells();
    return cells && cells.length > 0;
  }

  // Cell management
  selectCell(index: number, event: Event): void {
    event.stopPropagation();
    this.selectedCellIndex = index;
    this.cellSelected.emit({ cellIndex: index });
  }

  // Select component in cell
  selectComponentInCell(cellIndex: number, componentIndex: number, event: Event): void {
    event.stopPropagation();
    this.selectedCellIndex = cellIndex;
    this.componentInCellSelected.emit({ cellIndex, componentIndex });
  }

  isCellSelected(index: number): boolean {
    return this.selectedCellIndex === index;
  }

  getCellComponents(cell: GridCell): PageComponent[] {
    return cell.components || [];
  }

  hasCellComponents(cell: GridCell): boolean {
    return cell.components && cell.components.length > 0;
  }

  // Get component type display name
  getComponentTypeName(type: string): string {
    const names: { [key: string]: string } = {
      'text': 'Tekst',
      'media-gallery': 'Media Galerij',
      'products': 'Producten',
      'news': 'Nieuws',
      'projects': 'Projecten',
      'form': 'Formulier',
      'map': 'Kaart',
      'action': 'Action'
    };
    return names[type] || type;
  }

  // Get component icon
  getComponentIcon(type: string): string {
    const icons: { [key: string]: string } = {
      'text': 'document-text',
      'media-gallery': 'photograph',
      'products': 'cube',
      'news': 'newspaper',
      'projects': 'briefcase',
      'form': 'clipboard-list',
      'map': 'map-pin',
      'action': 'cursor-click'
    };
    return icons[type] || 'cube';
  }

  // Request to add component to cell (emits to parent)
  requestAddComponent(cellIndex: number, componentType: string, event: Event): void {
    event.stopPropagation();
    this.addComponentToCell.emit({ cellIndex, componentType });
  }

  // Generate unique ID for new cell
  generateCellId(): string {
    return 'cell-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }
}
